import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { AmountModel, ExternalPaymentModel, LedgersModel, PaymentsModel, PaymentStatusModel, PaymentVoucher } from 'src/app/core/models/payment-models';
import { StringHelperService } from 'src/app/core/utils/string-helper.service';
import { USER_INFORMATION, USER_LOGON_NAME } from 'src/app/modules/auth/shared/constants/auth.constant';
import { PaymentConstant } from 'src/app/modules/order/shared';
import { PaymentDocumentInvoiceConstant } from 'src/app/modules/order/shared/components/payment-document-invoice/shared/payment-document-invoice.constants';
import { selectAuthState } from 'src/app/store/auth/auth.selectors';
import { takeUntil } from "rxjs/operators";
import { BehaviorSubject, Subject } from 'rxjs';
import { OrderShoppingPaymentView } from 'src/app/modules/shopping/shared/views';
import { InvoiceOrganisationSearchModel, OrganisationModel } from 'src/app/core/models/organisation-model';
import { FocusingDirective } from 'src/app/shared/ui/forms/inputs/focusing.directive';
import { FocusingService } from 'src/app/shared/ui/forms/inputs/focusing.service';
import { CreditAccountOrganisationSearchView } from '../credit-account-search-table/credit-account-organisation-search.view';
import { InvoiceIndividualSearchModel } from 'src/app/core/models/individual-model';
import { GATEWAY_PAYMENT_CODE, GATEWAY_PAYMENT_STATUS, PaymentGatewayFactory } from 'src/app/core/services/payment-creditcard-services';
import { LoadingSpinnerService } from 'src/app/shared/layout/loading-spinner';
import { AlertBarService } from 'src/app/shared/layout/alertbar';
import { CurrencyReferenceModel } from 'src/app/core/models/reference-model/reference-general-model';
import { Select2Data } from 'src/app/shared/ui/forms/inputs/oops-select2';
import { DocumentInvoiceTypeSelectionComponent } from 'src/app/core/components/document-invoice-type-selection/document-invoice-type-selection.component';
import { PaymentDocumentInvoiceComponent } from 'src/app/modules/order/shared/components/payment-document-invoice/payment-document-invoice.component';
import { PaymentAddressView } from 'src/app/core/models/payment-models/payment-address.model';
import { OrganisationService } from 'src/app/core/services/organisation-services';
import { OrganisationFormOfPaymentModel } from 'src/app/core/models/organisation-model/organisation-form-of-payment.model';
import { PAYMENT_MESSAGE } from 'src/app/core/services/payment-services/payment-msg-constant';

const select2WithoutSearchOption = {
    placeholder: "",
    allowClear: false,
    minimumResultsForSearch: -1,
    width: 'auto',
    dropdownAutoWidth: true
}

@Component({
    selector: 'op-top-up-payment',
    templateUrl: './top-up-payment.component.html'
})
export class TopUpPaymentComponent implements OnInit, AfterViewInit {
    public readonly PAYMENT_CONSTANT = PaymentConstant;
    public readonly PAYMENT_DOCUMENT_CONSTANT = PaymentDocumentInvoiceConstant;
    private readonly alertStatus: string = "Payment";
    public readonly select2WithoutSearchOption = select2WithoutSearchOption;
    public readonly invoiceReceiverPanelName = 'Invoice Receiver';
    public readonly topupAmountErrorMessage: string = 'Top Up Amount is required.'
    
    public focused: boolean = false;
    public organisationId: string;

    public data: OrderShoppingPaymentView;
    public formOfPaymentTypeCode: string = "CC";
    public formOfPaymentCode: string;
    public invoiceReceiverType: string;
    public topUpOrganisation: CreditAccountOrganisationSearchView;
    public paymentOrganisation: InvoiceOrganisationSearchModel;
    public paymentIndividual: InvoiceIndividualSearchModel;
    public userInfo: any;
    
    public organisationTypeCode: string[];
    public formOfPaymentType$ = new BehaviorSubject<Select2Data[]>(null);
    public formOfPaymentSelectData: Select2Data[];

    public voucherNumber: string;
    public voucherPin: string;
    public processing: boolean = false;
    public organisationFormOfPaymentList: OrganisationFormOfPaymentModel[];

    private destroy$: Subject<boolean> = new Subject<boolean>();
    private paymentAddressRequired: boolean;

    get ownerOrganisation(): OrganisationModel {
        return this.organisationService.ownerOrganisation$.value;
    }

    @Input() userOrganisation: OrganisationModel;
    @Input() currencyReferences: CurrencyReferenceModel[];
    @Output() alertBarClick = new EventEmitter();
    @Output() paymentSuccess = new EventEmitter();
    @ViewChild(FocusingDirective) focusingDirective: FocusingDirective;
    @ViewChild(DocumentInvoiceTypeSelectionComponent) invoiceReceiverComponent: DocumentInvoiceTypeSelectionComponent;
    @ViewChild(PaymentDocumentInvoiceComponent) documentInvoiceComponent: PaymentDocumentInvoiceComponent;
    
    constructor(private stringUtils: StringHelperService,
        private authService: AuthService,
        private store: Store<any>,
        private focusingService: FocusingService,
        public alertBarService: AlertBarService,
        private changeDetector: ChangeDetectorRef,
        private spinner: LoadingSpinnerService,
        private paymentGatewayFactory: PaymentGatewayFactory,
        private organisationService: OrganisationService) {
            this.getOrganisationFormOfPayment();
        }


    ngOnInit(): void {
        this.initialPaymentView();
    }

    ngAfterViewInit(): void {
        if (this.topUpOrganisation) {
            this.fillSearchViewToForm(this.topUpOrganisation);
        }
    }

    private initialPaymentView() {
        this.data = {
            saleAmount: "0",
            saleCurrency: null,
            paymentAmount: "0",
            paymentCurrency: null
        };
    }

    public clearTopUpPaymentForm() {
        this.initialPaymentView();
        this.clearInvoiceReceiver();
    }

    get documentInvoiceSelected(): boolean {
        return this.formOfPaymentTypeCode === this.PAYMENT_DOCUMENT_CONSTANT.DOCUMENT_CODE && this.formOfPaymentCode === this.PAYMENT_DOCUMENT_CONSTANT.INVOICE_CODE;
    }

    get creditAccountIsSelected() {
        return this.formOfPaymentTypeCode === this.PAYMENT_CONSTANT.creditAccount;
    }

    public formOfPaymentTypeChange(formOfPaymentTypeCode: string | string[]) {
        this.formOfPaymentTypeCode = formOfPaymentTypeCode ? formOfPaymentTypeCode as string : null;
        this.getFormOfPaymentSelectData(this.formOfPaymentTypeCode);
        this.onInvoiceReceiverChange(null);
    }

    public formOfPaymentChange(code: string | string[]) {
        this.formOfPaymentCode = code ? code as string : null;
        this.checkPaymentAddressRequired(this.formOfPaymentTypeCode, this.formOfPaymentCode);
    }

    public onInvoiceReceiverChange(invoiceReceiverCode: string) {
        this.clearInvoiceReceiver();
        this.invoiceReceiverType = invoiceReceiverCode;
        this.focusInvoiceReceiver(invoiceReceiverCode);
    }

    private focusInvoiceReceiver(invoiceReceiverCode: string) {
        switch(invoiceReceiverCode) {
            case this.PAYMENT_DOCUMENT_CONSTANT.INDIVIDUAL_RECEIVER:
                this.documentInvoiceComponent?.individualComponent?.focusSearchCondition();
                break;
            case this.PAYMENT_DOCUMENT_CONSTANT.ORGANISATION_RECEIVER:
                this.documentInvoiceComponent?.organisationComponent?.focusSearchCondition();
                break;
        }
    }

    private getSessionItem(key: string) {
        return this.authService.sessionStorageGetItem(key)
    }

    public onPay() {
        const userNameInfo = JSON.parse(this.getSessionItem(USER_LOGON_NAME));
        this.userInfo = JSON.parse(this.getSessionItem(USER_INFORMATION));
        const paymentId = this.stringUtils.NewGuid();
        let commitBy = (userNameInfo.userAccountId) ? userNameInfo.userAccountId : this.getUserId();

        if (!this.validateInvoiceReceiver() || !this.validateTopUpForm()) {
            return;
        }
        let paymentAddress = this.documentInvoiceComponent?.getPaymentAddress();
        if (this.paymentAddressRequired && !this.validateOrganisationReceiver() && !paymentAddress) {
            return;
        }
        if (!this.validatePaymentAddress()) {
            this.alertBarService.show(this.alertStatus, this.documentInvoiceComponent?.getErrorMessagePaymentAddress());
            this.documentInvoiceComponent?.individualComponent?.focusAddress();
            return;
        }
        let ledger = [this.createLedger(this.userInfo, paymentId)];
        let payment = this.createPayment(this.userInfo, paymentId, commitBy, ledger);
        this.processPayment(payment, this.getPaymentVoucher(payment), paymentAddress)

    }

    private processPayment(paymentModel: PaymentsModel, paymentVoucher: PaymentVoucher, paymentAddress: PaymentAddressView) {
        this.spinner.show();
        let initialModel: ExternalPaymentModel = {
            organisationCode: this.userOrganisation.organisationCode,
            recordLocator: null,
            orderId: null,
            successQueryString: "",
            cancelQueryString: "",
            errorQueryString: "",
            amount: {
                currencyCode: paymentModel.sales.currencyCode,
                value: paymentModel.sales.amount
            },
            paymentVoucher: paymentVoucher,
            payments: paymentModel,
            paymentAddress: paymentAddress
        };

        this.handlePaymentGateWay(initialModel, paymentModel);
    }

    public showAlertSucess() {
        this.alertBarService.showInformation(this.alertStatus, PAYMENT_MESSAGE.success);
        let informationIntervalId = setInterval(() => {
            this.alertBarClick.emit();
            clearInterval(informationIntervalId);
        }, 5000);
    }

    private createLedger(userInfo: any, paymentId: string): LedgersModel {
        return {
            ledgerId: this.stringUtils.NewGuid(),
            parentLedgerId: null,
            paymentId: paymentId,
            organisationId: userInfo.organisationId,
            ledgerOrganisationId: this.topUpOrganisation.organisationId,
            ledgerIndividualId: null,
            orderId: null,
            ledgerTransactionCode: this.PAYMENT_CONSTANT.cr,
            sales: this.getAmount(parseFloat(this.data.saleAmount), this.data.saleCurrency),
            payment: this.getAmount(parseFloat(this.data.saleAmount), this.data.paymentCurrency),
            exchangeRate: null
        }
    }

    private createPayment(userInfo, paymentId, commitBy, ledgersModel) {
        let paymentModel: PaymentsModel = {
            paymentId: paymentId,
            referencePaymentId: null,
            organisationId: userInfo.organisationId,
            paymentOrganisationId: null,
            paymentIndividualId: null,
            paymentAddressId: null,
            paymentStatusCode: this.getDataPaymentStatus(this.formOfPaymentTypeCode),
            formOfPaymentTypeCode: this.formOfPaymentTypeCode,
            formOfPaymentCode: this.formOfPaymentCode,
            accountableDocumentTypeCode: null,
            documentNumber: null,
            documentDate: null,
            paymentDate: null,
            paymentDateTime: null,
            issueDateTime: null,
            externalReference: null,
            transactionReference: null,
            externalTransactionId: null,
            paymentToken: null,
            paymentText: null,
            commitBy: commitBy,
            sales: this.getAmount(parseFloat(this.data.saleAmount), this.data.saleCurrency),
            payment: this.getAmount(parseFloat(this.data.saleAmount), this.data.paymentCurrency),
            ledger: ledgersModel,
            topUpPaymentFlag: true,
            paymentAcquirerCode: null
        };
        this.setupDocumentPayment(paymentModel);
        return paymentModel;
    }

    private getAmount(amount: number, currencyCode: string): AmountModel {
        let model: AmountModel = {
            amount: amount,
            currencyCode: currencyCode
        }
        return model;
    }

    private getUserId() {
        this.store.select(selectAuthState).pipe(takeUntil(this.destroy$))
            .subscribe(state => {
                return state.userAccountId;
            });
    }

    public focusTopUp() {
        this.focusingService.focus(this.focusingDirective);
    }

    public clearInvoiceReceiver() {
        this.paymentIndividual = null;
        this.paymentOrganisation = null;
        this.documentInvoiceComponent?.clear();
    }

    public onInvoiceIndividualSelected(event) {
        if (event?.individualAddressId){
            this.alertBarService.hide();
        }
        this.paymentIndividual = event;
    }

    public onInvoiceOrganisationSelected(event) {
        this.paymentOrganisation = event;
    }

    public fillSearchViewToForm(view: CreditAccountOrganisationSearchView) {
        this.topUpOrganisation = view;
        this.data.saleAmount= "0";
        this.data.paymentCurrency = view?.currency;
        this.data.saleCurrency = view?.currency;
        this.changeDetector.detectChanges();
    }

    private getDataPaymentStatus(formOfPaymentTypeCode: string): string {
        switch (formOfPaymentTypeCode) {
            case this.PAYMENT_CONSTANT.cc:
            case this.PAYMENT_CONSTANT.eft:
            case this.PAYMENT_CONSTANT.paypal:
                return this.PAYMENT_CONSTANT.pending;
            case this.PAYMENT_DOCUMENT_CONSTANT.DOCUMENT_CODE:
                return this.PAYMENT_CONSTANT.issued;
            default:
                return this.PAYMENT_CONSTANT.paid;
        }
    }

    public setupDocumentPayment(dataPayment: PaymentsModel) {
        if (this.isInvoiceDocumentPayment(dataPayment.formOfPaymentTypeCode, dataPayment.formOfPaymentCode) || this.paymentAddressRequired) {
            switch (this.invoiceReceiverType) {  
                case this.PAYMENT_DOCUMENT_CONSTANT.ORGANISATION_RECEIVER:
                    dataPayment.paymentOrganisationId = this.paymentOrganisation.organisationId;
                    dataPayment.paymentAddressId = this.paymentOrganisation.organisationAddressId;
                    break;
                default:
                    dataPayment.paymentIndividualId = this.paymentIndividual.individualId;
                    dataPayment.paymentAddressId = this.paymentIndividual.individualAddressId;
                    break;
            }
        }
    }

    private getOrganisationFormOfPayment() {
       this.organisationService.getOrganisationFormOfPayments(null, null, null, false, true)
            .subscribe(
                (response: OrganisationFormOfPaymentModel[]) => {
                    const sorted = this.sortOrganisationFormOfPaymentAcquirerDisplaySequence(response);
                    this.organisationFormOfPaymentList = sorted;
                    this.getFormOfPaymentType(sorted);
                }
            )
    }

    private getFormOfPaymentType(response: OrganisationFormOfPaymentModel[]) {
        let formOfPaymentTypes = new Array<Select2Data>()
        response.forEach(item => {
            if (!formOfPaymentTypes?.find(fop => fop.id == item.formOfPaymentTypeCode)) {
                formOfPaymentTypes.push(new Select2Data(item.formOfPaymentTypeCode, item.formOfPaymentTypeName));
            }
        });

        const fopt = this.findDefaultFormOfPaymentTypeCode(response);
        this.formOfPaymentType$.next(formOfPaymentTypes);
        this.formOfPaymentTypeChange(fopt);
    }

    private findDefaultFormOfPaymentTypeCode(items: OrganisationFormOfPaymentModel[]): string {
        const primaryItem = items.find(x => x.primaryFlag);
        return primaryItem?.formOfPaymentTypeCode ?? items[0]?.formOfPaymentTypeCode;
    }

    private findDefaultFormOfPaymentCode(items: OrganisationFormOfPaymentModel[]): string {
        const primaryItem = items.find(x => x.primaryFlag);
        return primaryItem?.formOfPaymentCode ?? items[0]?.formOfPaymentCode;
    }

    private sortOrganisationFormOfPaymentAcquirerDisplaySequence(items: OrganisationFormOfPaymentModel[]): OrganisationFormOfPaymentModel[] {
        return items.sort((a, b) => a.organisationFormOfPaymentAcquirerDisplaySequence - b.organisationFormOfPaymentAcquirerDisplaySequence);
    }

    private getFormOfPaymentSelectData(typeCode: string) {
        if (!typeCode) {
            this.formOfPaymentSelectData = null;
            return
        }
        let fopList = this.organisationFormOfPaymentList?.filter(item => item.formOfPaymentTypeCode == typeCode);
        const fop = this.findDefaultFormOfPaymentCode(fopList);
        this.formOfPaymentChange(fop);
        this.formOfPaymentSelectData = fopList?.map(item => new Select2Data(item.formOfPaymentCode, item.formOfPaymentName))
    }

    private isInvoiceDocumentPayment(formOfPaymentTypeCode: string, formOfPaymentCode: string) {
        return formOfPaymentTypeCode == this.PAYMENT_DOCUMENT_CONSTANT.DOCUMENT_CODE && formOfPaymentCode == this.PAYMENT_DOCUMENT_CONSTANT.INVOICE_CODE
    }

    private validateInvoiceReceiver(): boolean {
        if (!this.isInvoiceDocumentPayment(this.formOfPaymentTypeCode, this.formOfPaymentCode)) {
            return true;
        }
        if (!this.invoiceReceiverComponent) {
             this.alertBarService.show(this.alertStatus, 'Invoice Receiver Information is required!');
             return false;
        }
        if (!this.invoiceReceiverComponent.validate()) {
            this.alertBarService.show(this.alertStatus, this.invoiceReceiverComponent.getErrorMessage());
            return false;
        } else if (!(this.validateIndividualReciever() || this.validateOrganisationReceiver())) {
            this.alertBarService.show(this.invoiceReceiverPanelName, 'Invoice Receiver Information is required.');
            this.focusInvoiceReceiver(this.invoiceReceiverType);
            return false;
        }
        return true;
    }

    private validateIndividualReciever(): boolean {
        return this.invoiceReceiverType == this.PAYMENT_DOCUMENT_CONSTANT.INDIVIDUAL_RECEIVER && !!(this.documentInvoiceComponent?.validateIndividualPaymentAddress(this.paymentAddressRequired));
    }

    private validateOrganisationReceiver(): boolean {
        return this.invoiceReceiverType == this.PAYMENT_DOCUMENT_CONSTANT.ORGANISATION_RECEIVER && !!(this.paymentOrganisation);
    }

    get displayVoucherForm(): boolean {
        return this.formOfPaymentTypeCode == this.PAYMENT_CONSTANT.voucher && this.formOfPaymentCode == this.PAYMENT_CONSTANT.sales;
    }

    private getPaymentVoucher(dataPayment: PaymentsModel): PaymentVoucher {
        if (dataPayment.formOfPaymentTypeCode == this.PAYMENT_CONSTANT.voucher && dataPayment.formOfPaymentCode == this.PAYMENT_CONSTANT.sales) {
            return {
                paymentCode: null,
                voucherId: null,
                voucherNumber: this.voucherNumber,
                voucherPin: this.voucherPin
            }
        }
        return null;
    }

    public validateTopUpForm() {
        this.processing = true;
        if (this.validateTopupAmount()) {
            this.processing = false;
            return true;
        }
        this.alertBarService.show(this.alertStatus, this.topupAmountErrorMessage);
        return false;
    }

    public validateTopupAmount() {
        if (Number(this.data.saleAmount) > 0) {
            return true;
        }
        return false;
    }

    get displayAmountError(): boolean {
        return this.processing && !this.validateTopupAmount();
    }

    private handlePaymentGateWay(initialModel: ExternalPaymentModel, paymentModel: PaymentsModel) {
        const paymentAcquirerCode = this.getPaymentGatewayCode(initialModel);
        const gatewayProvider = this.paymentGatewayFactory.create(paymentAcquirerCode);
        if (!gatewayProvider) {
            this.spinner.hide();
            this.changeDetector.detectChanges();
            this.alertBarService.show(this.alertStatus, 'No payment gateway setup.');
            return;
        }
        gatewayProvider.processPayment(initialModel)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response: PaymentStatusModel) => {
                if (!response) {
                    return;
                }
                this.handleGeneralPaymentResponse(response);
                this.spinner.hide();

            }, () => this.spinner.hide());
    }

    private handleGeneralPaymentResponse(response: PaymentStatusModel) {
        if (response.paymentStatus === PaymentConstant.paid || response.status === GATEWAY_PAYMENT_STATUS.SUCCESS) {
            this.onPaymentSuccess();
            return;
        }
        if (response.status === GATEWAY_PAYMENT_STATUS.CANCEL || response.status == null) {
            this.onPaymentFail(this.alertStatus, PAYMENT_MESSAGE.cancelled);
            return;
        }
        if (response.status === GATEWAY_PAYMENT_STATUS.ERROR) {
            this.onPaymentFail(this.alertStatus, PAYMENT_MESSAGE.error);
            return;
        }
        this.onPaymentInProcess(this.alertStatus, PAYMENT_MESSAGE.inProcess);
    }

    private onPaymentSuccess() {
        this.alertBarService.showInformation(this.alertStatus, PAYMENT_MESSAGE.success);
        this.paymentSuccess.emit();
    }

    private onPaymentFail(alertName: string, alertMessage: string) {
        this.alertBarService.show(alertName, alertMessage);
    }

    private onPaymentInProcess(alertName: string, alertMessage: string) {
        this.alertBarService.showWarning(`${alertName}: ${alertMessage}`);
        this.paymentSuccess.emit();
    }

    private getPaymentGatewayCode(initialModel: ExternalPaymentModel): string {
        const organisationFormOfPayment = this.organisationFormOfPaymentList?.find(item => item.formOfPaymentTypeCode == initialModel.payments.formOfPaymentTypeCode
            && item.formOfPaymentCode == initialModel.payments.formOfPaymentCode);
        return organisationFormOfPayment?.paymentAcquirerCode == this.ownerOrganisation?.organisationCode
            ? GATEWAY_PAYMENT_CODE.DATATRANS : organisationFormOfPayment?.paymentAcquirerCode;
    }

    private checkPaymentAddressRequired(formOfPaymentTypeCode: string, formOfPaymentCode: string) {
        let formOfPayment = this.organisationFormOfPaymentList.find(x => x.formOfPaymentTypeCode == formOfPaymentTypeCode && x.formOfPaymentCode == formOfPaymentCode);
        if (formOfPayment) {
            this.paymentAddressRequired = formOfPayment.requireAddressFlag;
            if (!formOfPayment.requireAddressFlag) {
                this.alertBarService.hide();
            }
            return;
        }
        this.alertBarService.hide();
        this.paymentAddressRequired = false;
        this.documentInvoiceComponent.setAddressPaymentRequired(this.paymentAddressRequired);
        if (this.paymentAddressRequired) {
            this.documentInvoiceComponent.individualComponent.detailComponent.paymentAddressRequired = this.paymentAddressRequired;
            this.documentInvoiceComponent?.individualComponent?.focusAddress();
        }
        this.changeDetector.detectChanges();
    }

    private validatePaymentAddress(): boolean {
        if (!this.paymentAddressRequired) {
            return true;
        }
        if (this.isInvoiceDocumentPayment(this.formOfPaymentTypeCode, this.formOfPaymentCode)) {
            return true;
        }
        return this.documentInvoiceComponent?.validateIndividualPaymentAddress(this.paymentAddressRequired);
    }

    public hideDocumentInvoice(){
        if (this.paymentAddressRequired) {
            return false;
        }
        if (this.isInvoiceDocumentPayment(this.formOfPaymentTypeCode, this.formOfPaymentCode)) {
            return false;
        }
        return true;
    }	
}